import axios from 'axios';
import {
  Advertiser,
  Campaign,
  InsertionOrder,
  LineItem,
  Creative,
  SyncStatus
} from '../types/ads';
import { API_BASE_URL } from '../config/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Demo account is read-only: block every write from the client so no Save/Launch/
// Pause/Edit action fires (the server enforces this too — this just avoids the round
// trip and shows a friendly notice instead of a 403 error). Auth writes stay allowed.
export function isDemoReadonly(): boolean {
  return localStorage.getItem('sense_is_demo') === '1';
}

let _demoToastAt = 0;
function showDemoToast() {
  const now = Date.now();
  if (now - _demoToastAt < 2500) return;   // throttle repeated clicks
  _demoToastAt = now;
  try {
    const el = document.createElement('div');
    el.textContent = 'Demo mode — this is a read-only account. Changes are disabled.';
    el.style.cssText = [
      'position:fixed', 'left:50%', 'bottom:32px', 'transform:translateX(-50%)',
      'background:#1f2430', 'color:#fff', 'padding:12px 20px', 'border-radius:10px',
      'font:500 14px/1.4 system-ui,sans-serif', 'box-shadow:0 8px 30px rgba(0,0,0,.25)',
      'z-index:99999', 'max-width:90vw', 'opacity:0', 'transition:opacity .2s',
    ].join(';');
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 250); }, 2600);
  } catch { /* no-op */ }
}

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  if (isDemoReadonly() && !['get', 'head', 'options'].includes(method) && !url.includes('/auth/')) {
    showDemoToast();
    return Promise.reject(new axios.Cancel('Demo account is read-only'));
  }
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses - try refresh before logging out
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;

    if (status === 401 && !originalRequest._retry) {
      // Check if account is blocked/paused - don't retry
      const detail = error.response?.data?.detail || '';
      if (typeof detail === 'string' && (detail.includes('paused') || detail.includes('blocked') || detail.includes('suspended'))) {
        localStorage.removeItem('access_token');
        window.location.replace('/login');
        return new Promise(() => {});
      }

      if (isRefreshing) {
        // Another request is already refreshing - queue this one
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          const newToken = data.access_token;
          localStorage.setItem('access_token', newToken);

          // Retry queued requests
          onTokenRefreshed(newToken);
          isRefreshing = false;

          // Retry the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch {
        // Refresh failed
      }

      // Refresh failed - logout
      isRefreshing = false;
      refreshSubscribers = [];
      localStorage.removeItem('access_token');
      window.location.replace('/login');
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

class ApiService {
  async getAdvertisers(): Promise<Advertiser[]> {
    const response = await api.get('/ads/tree/advertisers');
    return response.data.data || [];
  }

  async getCampaigns(advertiserId: string): Promise<Campaign[]> {
    const response = await api.get(`/ads/tree/campaigns?advertiser_id=${advertiserId}`);
    return response.data.data || [];
  }

  async getInsertionOrders(campaignId: string): Promise<InsertionOrder[]> {
    const response = await api.get(`/ads/tree/insertion-orders?campaign_id=${campaignId}`);
    return response.data.data || [];
  }

  async getLineItems(insertionOrderId: string): Promise<LineItem[]> {
    const response = await api.get(`/ads/tree/line-items?insertion_order_id=${insertionOrderId}`);
    return response.data.data || [];
  }

  async getCreatives(lineItemId?: string, advertiserId?: string): Promise<Creative[]> {
    let url = '/ads/tree/creatives';
    if (lineItemId) {
      url += `?line_item_id=${lineItemId}`;
    } else if (advertiserId) {
      url += `?advertiser_id=${advertiserId}`;
    }
    const response = await api.get(url);
    return response.data.data || [];
  }

  async getSyncStatus(): Promise<SyncStatus> {
    const response = await api.get('/ads/tree/sync-status');
    return response.data;
  }

  async globalSearch(query: string): Promise<any> {
    const response = await api.get(`/ads/search/global?query=${query}`);
    return response.data;
  }
}

// ============================================
// Admin Client Management API
// ============================================

interface ClientUser {
  id: number;
  email: string;
  status: string;
  password?: string;
  first_name?: string;
  last_name?: string;
  last_login_at?: string;
  parent_user_id?: number | null;
  manager_email?: string;
}

export interface Manager {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  regional_office_id: number | null;
}

export interface Client {
  id: number;
  advertiser_id: string;
  display_name: string;
  entity_status: string;
  regional_office_id: number | null;
  regional_office_name: string;
  markup: number | null;
  discount_percent: number;
  creation_status: string;
  creation_error?: string | null;
  campaigns_count: number;
  client_user: ClientUser | null;
  /** First of `managers`, kept for callers that show a single name. */
  manager_user: { id: number; email: string; first_name: string | null; last_name: string | null } | null;
  /** The client's whole manager team — a client may be run by several people. */
  managers: { id: number; email: string; first_name: string | null; last_name: string | null; role: string }[];
  agency_id: number | null;
  agency_name: string | null;
  is_self_service: boolean;
  has_freelancer: boolean;
  freelancer_user: { id: number; email: string; first_name: string | null; last_name: string | null } | null;
  client_markup: number | null;
  billing_model: string;
  // Connected external platforms (Sense Max readiness) — drives the "Max" column.
  external_platforms?: string[];
}

interface ClientsListResponse {
  clients: Client[];
  total: number;
  limit: number;
  offset: number;
}

interface CreateClientRequest {
  name: string;
  domain?: string;
  regional_office_id?: number;
  agency_id?: number;
  legal_name?: string;
  tax_id?: string;
  legal_address?: string;
  contract_number?: string;
}

interface CreateClientResponse {
  success: boolean;
  advertiser_db_id: number;
  advertiser_name: string;
  creation_status: string;
  user_id: number;
  user_email: string;
  password: string;
  regional_office_id: number | null;
  message: string;
}

interface ResetPasswordResponse {
  success: boolean;
  user_id: number;
  email: string;
  password: string;
  message: string;
}

export const clientsApi = {
  list: async (params?: {
    search?: string;
    regional_office_id?: number;
    agency_id?: number;
    unassigned?: boolean;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ClientsListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.regional_office_id) searchParams.append('regional_office_id', params.regional_office_id.toString());
    if (params?.agency_id) searchParams.append('agency_id', params.agency_id.toString());
    if (params?.unassigned) searchParams.append('unassigned', 'true');
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const response = await api.get(`/api/admin/clients?${searchParams.toString()}`);
    return response.data;
  },

  get: async (advertiserId: string): Promise<Client & { timezone?: string; creation_error?: string }> => {
    const response = await api.get(`/api/admin/clients/${advertiserId}`);
    return response.data;
  },

  create: async (data: CreateClientRequest): Promise<CreateClientResponse> => {
    const response = await api.post('/api/admin/clients', data);
    return response.data;
  },

  updateOffice: async (advertiserId: string, regionalOfficeId: number | null): Promise<any> => {
    const response = await api.patch(`/api/admin/clients/${advertiserId}`, {
      regional_office_id: regionalOfficeId
    });
    return response.data;
  },

  update: async (advertiserId: string, data: { display_name?: string; regional_office_id?: number | null; manager_id?: number | null; manager_ids?: number[]; agency_id?: number | null; discount_percent?: number; client_markup?: number | null; google_ads_customer_id?: string | null; google_login_customer_id?: string | null; meta_ad_account_id?: string | null; tiktok_advertiser_id?: string | null; yandex_client_login?: string | null; linkedin_ad_account_id?: string | null; meta_page_id?: string | null; meta_page_name?: string | null; tiktok_identity_id?: string | null; tiktok_identity_type?: string | null; tiktok_identity_name?: string | null; linkedin_org_id?: string | null; linkedin_org_name?: string | null; legal_name?: string; tax_id?: string; legal_address?: string; contract_number?: string }): Promise<any> => {
    const response = await api.patch(`/api/admin/clients/${advertiserId}`, data);
    return response.data;
  },

  // Fully disconnect an external platform: clears both the cabinet id and the
  // stored OAuth token for that platform.
  disconnectExternal: async (advertiserId: string, platform: 'google' | 'meta' | 'tiktok' | 'yandex' | 'linkedin'): Promise<any> => {
    const response = await api.delete(`/api/admin/clients/${advertiserId}/external/${platform}`);
    return response.data;
  },

  retryCreation: async (advertiserId: string): Promise<{
    success: boolean;
    advertiser_id?: string;
    creation_status?: string;
    creation_error?: string | null;
    skipped?: boolean;
    reason?: string;
  }> => {
    const response = await api.post(`/api/admin/clients/${advertiserId}/retry-creation`);
    return response.data;
  },

  // Click reconciliation periods (admin only)
  setClickAdjustment: async (advertiserId: string, data: { effective_from: string; factor: number; reason: string }): Promise<any> => {
    const response = await api.post(`/api/admin/clients/${advertiserId}/click-adjustments`, data);
    return response.data;
  },

  deleteClickAdjustment: async (advertiserId: string, effectiveFrom: string): Promise<any> => {
    const response = await api.delete(`/api/admin/clients/${advertiserId}/click-adjustments/${effectiveFrom}`);
    return response.data;
  },

  getCreationStatus: async (advertiserId: string): Promise<{
    advertiser_id: string;
    creation_status: string | null;
    creation_error: string | null;
    is_ready: boolean;
  }> => {
    const response = await api.get(`/api/sense/advertisers/${advertiserId}/creation-status`);
    return response.data;
  },

  triggerExternalSync: async (advertiserId: string): Promise<{
    advertiser_id: string;
    results: Array<{
      source: string;
      external_campaigns_fetched: number;
      matched_sense_campaigns: number;
      rows_written: number;
      unmatched_external_names: string[];
    }>;
  }> => {
    const response = await api.post(`/api/admin/clients/${advertiserId}/external/sync`);
    return response.data;
  },

  getMyMarkup: async (): Promise<{ default_client_markup: number }> => {
    const response = await api.get('/api/admin/clients/my-markup');
    return response.data;
  },

  updateMyMarkup: async (markup: number): Promise<any> => {
    const response = await api.patch('/api/admin/clients/my-markup', { default_client_markup: markup });
    return response.data;
  },

  /** Eligible managers for a client. Pass the client's agency to include that
   *  agency's own managers and buyers — a client of an agency is run by its people. */
  getManagers: async (officeId?: number, agencyId?: number | null): Promise<{ managers: Manager[] }> => {
    const qs = new URLSearchParams();
    if (officeId !== undefined) qs.set('office_id', String(officeId));
    if (agencyId) qs.set('agency_id', String(agencyId));
    const params = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/api/admin/clients/managers${params}`);
    return response.data;
  },

  getUser: async (advertiserId: string): Promise<{ user: ClientUser | null }> => {
    const response = await api.get(`/api/admin/clients/${advertiserId}/user`);
    return response.data;
  },

  resetPassword: async (advertiserId: string): Promise<ResetPasswordResponse> => {
    const response = await api.post(`/api/admin/clients/${advertiserId}/user/reset-password`);
    return response.data;
  },

  bulkAssign: async (advertiserIds: string[], regionalOfficeId: number | null): Promise<any> => {
    const response = await api.post('/api/admin/clients/bulk-assign', {
      advertiser_ids: advertiserIds,
      regional_office_id: regionalOfficeId
    });
    return response.data;
  },

  archive: async (advertiserId: string): Promise<any> => {
    const response = await api.post(`/api/admin/clients/${advertiserId}/archive`);
    return response.data;
  },

  restore: async (advertiserId: string): Promise<any> => {
    const response = await api.post(`/api/admin/clients/${advertiserId}/restore`);
    return response.data;
  },

  remove: async (advertiserId: string): Promise<any> => {
    const response = await api.post(`/api/admin/clients/${advertiserId}/delete`);
    return response.data;
  }
};

// ============================================
// Agencies API
// ============================================

export interface Agency {
  id: number;
  name: string;
  regional_office_id: number | null;
  status: string;
  manager_user_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  office_name: string | null;
  manager_email: string | null;
  manager_first_name: string | null;
  manager_last_name: string | null;
  user_count: number;
  client_count: number;
  discount?: { discount_percent: number; source: string; spend_3m?: number | null; stage_id?: number | null; effective_from?: string | null };
  default_client_markup: number;
  billing_model: string;
  /** First of `admins`, kept for callers that show a single name. */
  admin_email: string | null;
  admin_first_name: string | null;
  admin_last_name: string | null;
  /** Every head of the agency — several can run it as equals. */
  admins: { id: number; email: string; first_name: string | null; last_name: string | null; status: string }[];
}

interface CreateAgencyRequest {
  name: string;
  regional_office_id?: number;
  manager_user_id?: number;
  client_ids?: string[];
  default_client_markup?: number;
  billing_model?: string;
  legal_name?: string;
  tax_id?: string;
  legal_address?: string;
  contract_number?: string;
}

interface UpdateAgencyRequest {
  name?: string;
  regional_office_id?: number;
  manager_user_id?: number;
  client_ids?: string[];
  default_client_markup?: number;
  billing_model?: string;
  legal_name?: string;
  tax_id?: string;
  legal_address?: string;
  contract_number?: string;
}

export const agenciesApi = {
  list: async (params?: { status?: string; regional_office_id?: number }): Promise<{ agencies: Agency[] }> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.regional_office_id) searchParams.append('regional_office_id', params.regional_office_id.toString());
    const response = await api.get(`/api/admin/agencies?${searchParams.toString()}`);
    return response.data;
  },

  get: async (id: number): Promise<Agency> => {
    const response = await api.get(`/api/admin/agencies/${id}`);
    return response.data;
  },

  create: async (data: CreateAgencyRequest): Promise<any> => {
    const response = await api.post('/api/admin/agencies', data);
    return response.data;
  },

  update: async (id: number, data: UpdateAgencyRequest): Promise<any> => {
    const response = await api.patch(`/api/admin/agencies/${id}`, data);
    return response.data;
  },

  archive: async (id: number): Promise<any> => {
    const response = await api.post(`/api/admin/agencies/${id}/archive`);
    return response.data;
  },

  restore: async (id: number): Promise<any> => {
    const response = await api.post(`/api/admin/agencies/${id}/restore`);
    return response.data;
  },

  remove: async (id: number): Promise<any> => {
    const response = await api.post(`/api/admin/agencies/${id}/delete`);
    return response.data;
  },
};

// ============================================
// Discounts API
// ============================================

export const commissionsApi = {
  getTiers: async (): Promise<{ tiers: Record<string, Array<{ id: number; model_type: string; min_spend: number; commission_percent: number }>> }> => {
    const response = await api.get('/api/admin/commission-tiers');
    return response.data;
  },

  updateTiers: async (tiers: Array<{ model_type: string; min_spend: number; commission_percent: number }>): Promise<any> => {
    const response = await api.put('/api/admin/commission-tiers', { tiers });
    return response.data;
  },

  getEntityCommission: async (entityType: string, entityId: number | string): Promise<any> => {
    const response = await api.get(`/api/admin/entity-commission/${entityType}/${entityId}`);
    return response.data;
  },

  bulk: async (entityType: string, entityIds: Array<number | string>): Promise<{ results: Record<string, { commission_percent: number; source: string; model_type: string; spend_90d: number | null }> }> => {
    const response = await api.post('/api/admin/entity-commission/bulk', { entity_type: entityType, entity_ids: entityIds.map(String) });
    return response.data;
  },

  setEntityModel: async (entityType: string, entityId: number | string, modelType: string): Promise<any> => {
    const response = await api.put(`/api/admin/entity-commission/${entityType}/${entityId}/model`, { model_type: modelType });
    return response.data;
  },

  addEntityStage: async (entityType: string, entityId: number | string, data: { commission_percent: number; effective_from: string }): Promise<any> => {
    const response = await api.post(`/api/admin/entity-commission/${entityType}/${entityId}`, data);
    return response.data;
  },

  deleteEntityStage: async (entityType: string, entityId: number | string, stageId: number): Promise<any> => {
    const response = await api.delete(`/api/admin/entity-commission/${entityType}/${entityId}/${stageId}`);
    return response.data;
  },

  // Per-entity Sense minimum budget share (0–35%). Floor the Sense slice can be dragged to in a
  // manual Sense Max budget split; inherits client → agency/freelancer → default (15%).
  getSenseMin: async (entityType: string, entityId: number | string): Promise<{ own: number | null; current: { min_percent: number; source: string; level: string | null }; default_percent: number }> => {
    const response = await api.get(`/api/admin/sense-min/${entityType}/${entityId}`);
    return response.data;
  },

  setSenseMin: async (entityType: string, entityId: number | string, minPercent: number): Promise<any> => {
    const response = await api.put(`/api/admin/sense-min/${entityType}/${entityId}`, { min_percent: minPercent });
    return response.data;
  },

  clearSenseMin: async (entityType: string, entityId: number | string): Promise<any> => {
    const response = await api.delete(`/api/admin/sense-min/${entityType}/${entityId}`);
    return response.data;
  },

  // Per-client account-ownership override (own vs client accounts).
  getClientOwnership: async (advertiserId: string): Promise<{ advertiser_id: string; model_type: string; is_override: boolean; inherited: { entity_type: string; entity_id: string; model_type: string } }> => {
    const response = await api.get(`/api/admin/client-ownership/${advertiserId}`);
    return response.data;
  },

  setClientOwnership: async (advertiserId: string, modelType: string): Promise<any> => {
    const response = await api.put(`/api/admin/client-ownership/${advertiserId}`, { model_type: modelType });
    return response.data;
  },

  clearClientOwnership: async (advertiserId: string): Promise<any> => {
    const response = await api.delete(`/api/admin/client-ownership/${advertiserId}`);
    return response.data;
  },
};

// Per-client × per-platform RESELLER markup (baked into external spend on OWN cabinets).
export interface ResellerMarkupChannel {
  current_markup: number;
  stages: Array<{ id: number; markup_percent: number; effective_from: string; is_active: boolean }>;
  daily: Array<{ date: string; markup_percent: number }>;
}
export const resellerMarkupApi = {
  get: async (advertiserId: string): Promise<{ advertiser_id: string; model_type: string; channels: Record<string, ResellerMarkupChannel> }> => {
    const response = await api.get(`/api/admin/reseller-markup/${advertiserId}`);
    return response.data;
  },

  addStage: async (advertiserId: string, channel: string, data: { markup_percent: number; effective_from: string }): Promise<any> => {
    const response = await api.post(`/api/admin/reseller-markup/${advertiserId}/${channel}`, data);
    return response.data;
  },

  deleteStage: async (advertiserId: string, channel: string, stageId: number): Promise<any> => {
    const response = await api.delete(`/api/admin/reseller-markup/${advertiserId}/${channel}/${stageId}`);
    return response.data;
  },
};

export const discountsApi = {
  getTiers: async (): Promise<{ tiers: Record<string, Array<{ id: number; tier_type: string; min_spend: number; discount_percent: number }>> }> => {
    const response = await api.get('/api/admin/discount-tiers');
    return response.data;
  },

  updateTiers: async (tiers: Array<{ tier_type: string; min_spend: number; discount_percent: number }>): Promise<any> => {
    const response = await api.put('/api/admin/discount-tiers', { tiers });
    return response.data;
  },

  getEntityDiscount: async (entityType: string, entityId: number): Promise<any> => {
    const response = await api.get(`/api/admin/entity-discount/${entityType}/${entityId}`);
    return response.data;
  },

  addEntityStage: async (entityType: string, entityId: number, data: { discount_percent: number; effective_from: string }): Promise<any> => {
    const response = await api.post(`/api/admin/entity-discount/${entityType}/${entityId}`, data);
    return response.data;
  },

  deleteEntityStage: async (entityType: string, entityId: number, stageId: number): Promise<any> => {
    const response = await api.delete(`/api/admin/entity-discount/${entityType}/${entityId}/${stageId}`);
    return response.data;
  },
};

// ============================================
// Billing API
// ============================================

export interface BillingAccount {
  id: number;
  advertiser_id: string;
  billing_model: string;
  balance: number;
  currency: string;
  low_balance_threshold: number;
  auto_pause_enabled: boolean;
  is_paused_by_billing: boolean;
  advertiser_name?: string;
  office_name?: string;
  /** Manually hidden from the default billing list (admin action). */
  hidden?: boolean;
  /** True when EITHER card or bank rail is usable. Backward-compat alias. */
  payment_available?: boolean;
  /** Card top-up via the Top-up button. */
  card_available?: boolean;
  /** Bank-transfer proforma generation. */
  bank_available?: boolean;
  card_provider?: string | null;
  bank_provider?: string | null;
  created_at: string;
}

export interface BillingTransaction {
  id: number;
  billing_account_id: number;
  type: string;
  amount: number;
  balance_after: number;
  currency: string;
  reference_type: string | null;
  reference_id: string | null;
  spend_date: string | null;
  description: string | null;
  created_at: string;
}

export interface Invoice {
  id: number;
  invoice_number: string;
  advertiser_id: string | null;
  agency_id: number | null;
  scope: string;
  advertiser_name?: string;
  agency_name?: string;
  office_name?: string;
  type: string;
  status: string;
  subtotal: number;
  vat_rate: number;
  vat_amount: number;
  total: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  issued_at: string | null;
  paid_at: string | null;
  created_at: string;
  /** Billing model of the linked account: 'prepaid' | 'postpaid'. Drives
   *  whether Mark Paid is shown (prepaid invoices are auto-closed). */
  billing_model?: string;
}

export interface BroadcastCertificate {
  id: number;
  advertiser_id: string;
  advertiser_name?: string;
  office_name?: string;
  agency_name?: string;
  freelancer_name?: string;
  period_start: string;
  period_end: string;
  include_financials: boolean;
  total_impressions: number;
  total_clicks: number;
  total_spend: number;
  cert_type: string;
  currency: string;
  created_at: string;
}

export const billingApi = {
  // Accounts
  listAccounts: async (): Promise<{ accounts: BillingAccount[] }> => {
    const response = await api.get('/api/billing/accounts');
    return response.data;
  },

  getAccount: async (advertiserId: string): Promise<BillingAccount> => {
    const response = await api.get(`/api/billing/accounts/${advertiserId}`);
    return response.data;
  },

  createAccount: async (data: {
    advertiser_id: string; billing_model?: string; currency?: string;
    low_balance_threshold?: number; auto_pause_enabled?: boolean;
  }): Promise<any> => {
    const response = await api.post('/api/billing/accounts', data);
    return response.data;
  },

  updateAccount: async (advertiserId: string, data: {
    billing_model?: string; currency?: string; low_balance_threshold?: number;
    auto_pause_enabled?: boolean;
  }): Promise<any> => {
    const response = await api.patch(`/api/billing/accounts/${advertiserId}`, data);
    return response.data;
  },

  // Top-up & Adjustment
  topup: async (data: { advertiser_id: string; amount: number; description?: string; topup_type?: string; topup_date?: string }): Promise<{ success: boolean; new_balance: number }> => {
    const response = await api.post('/api/billing/topup', data);
    return response.data;
  },

  adjustment: async (data: { advertiser_id: string; amount: number; description: string }): Promise<{ success: boolean; new_balance: number }> => {
    const response = await api.post('/api/billing/adjustment', data);
    return response.data;
  },

  hideAccount: async (advertiserId: string): Promise<{ success: boolean; hidden: boolean }> => {
    const response = await api.post(`/api/billing/accounts/${advertiserId}/hide`);
    return response.data;
  },

  unhideAccount: async (advertiserId: string): Promise<{ success: boolean; hidden: boolean }> => {
    const response = await api.post(`/api/billing/accounts/${advertiserId}/unhide`);
    return response.data;
  },

  // Transactions
  getTransactions: async (advertiserId: string, params?: { limit?: number; offset?: number }): Promise<{
    transactions: BillingTransaction[]; total: number;
  }> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const response = await api.get(`/api/billing/transactions/${advertiserId}?${searchParams.toString()}`);
    return response.data;
  },

  // Spend sync
  triggerSpendSync: async (): Promise<any> => {
    const response = await api.post('/api/billing/spend-sync/trigger');
    return response.data;
  },

  // Currencies
  getCurrencies: async (): Promise<{ currencies: Array<{ code: string; name: string; symbol: string }> }> => {
    const response = await api.get('/api/billing/currencies');
    return response.data;
  },

  // Invoices
  listInvoices: async (params?: { advertiser_id?: string; status?: string; type?: string; limit?: number; offset?: number }): Promise<{ invoices: Invoice[]; totals?: { currency: string; is_sense_staff: boolean } }> => {
    const searchParams = new URLSearchParams();
    if (params?.advertiser_id) searchParams.append('advertiser_id', params.advertiser_id);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.type) searchParams.append('type', params.type);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const response = await api.get(`/api/billing/invoices?${searchParams.toString()}`);
    return response.data;
  },

  createInvoice: async (data: {
    advertiser_id?: string; agency_id?: number; scope?: string;
    type: string; amount?: number;
    period_start?: string; period_end?: string; description?: string;
    buyer_details?: Record<string, string>;
  }): Promise<any> => {
    const response = await api.post('/api/billing/invoices', data);
    return response.data;
  },

  getInvoice: async (id: number): Promise<Invoice> => {
    const response = await api.get(`/api/billing/invoices/${id}`);
    return response.data;
  },

  updateInvoiceStatus: async (id: number, status: string): Promise<any> => {
    const response = await api.patch(`/api/billing/invoices/${id}/status`, { status });
    return response.data;
  },

  deleteInvoice: async (id: number): Promise<any> => {
    const response = await api.delete(`/api/billing/invoices/${id}`);
    return response.data;
  },

  downloadInvoicePdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/api/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  regenerateInvoice: async (id: number): Promise<{ ok: boolean; status?: string }> => {
    const response = await api.post(`/api/billing/invoices/${id}/regenerate`);
    return response.data;
  },

  // Certificates
  listCertificates: async (params?: { advertiser_id?: string; limit?: number; offset?: number }): Promise<{ certificates: BroadcastCertificate[] }> => {
    const searchParams = new URLSearchParams();
    if (params?.advertiser_id) searchParams.append('advertiser_id', params.advertiser_id);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const response = await api.get(`/api/billing/certificates?${searchParams.toString()}`);
    return response.data;
  },

  createCertificate: async (data: {
    advertiser_id: string; period_start: string; period_end: string;
    include_financials: boolean;
  }): Promise<any> => {
    const response = await api.post('/api/billing/certificates', data);
    return response.data;
  },

  downloadCertificatePdf: async (id: number): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/api/billing/certificates/${id}/pdf`, { responseType: 'blob' });
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = match ? match[1] : `certificate_${id}.pdf`;
    return { blob: response.data, filename };
  },
};

// ─── Payment methods (provider-agnostic) ──────────────────────

export interface PaymentMethod {
  id: number;
  provider: string;            // 'revolut' | 'monobank' | ...
  method_type: string;         // 'card' | 'sepa_dd'
  brand: string | null;        // 'VISA' | 'MASTERCARD' | ...
  last4: string | null;
  label: string | null;
  is_default_for_topup: boolean;
  status: string;
  created_at: string;
  metadata?: Record<string, any> | null;
  payment_profile_id?: number | null;
  payment_profile_name?: string | null;
  /** True when the card's issuing profile no longer matches the office's
   *  current profile — usable as a backup signal even before status flips. */
  profile_mismatch?: boolean;
}

export interface AutoTopupSettings {
  enabled: boolean;
  threshold: number;
  topup_amount: number;
  currency: string;
  has_default_method: boolean;
}

export const paymentMethodsApi = {
  list: async (advertiserId: string): Promise<PaymentMethod[]> => {
    const response = await api.get(`/api/billing/payment-methods/${advertiserId}`);
    return response.data;
  },
  remove: async (methodId: number): Promise<void> => {
    await api.delete(`/api/billing/payment-methods/${methodId}`);
  },
  setDefault: async (methodId: number): Promise<void> => {
    await api.post(`/api/billing/payment-methods/${methodId}/set-default`);
  },
  getAutoTopup: async (advertiserId: string): Promise<AutoTopupSettings> => {
    const response = await api.get(`/api/billing/auto-topup/${advertiserId}`);
    return response.data;
  },
  updateAutoTopup: async (
    advertiserId: string,
    data: { enabled: boolean; threshold: number; topup_amount: number },
  ): Promise<AutoTopupSettings> => {
    const response = await api.put(`/api/billing/auto-topup/${advertiserId}`, data);
    return response.data;
  },
};

// ─── Revolut Merchant ─────────────────────────────────────────

export interface RevolutTopupResponse {
  checkout_url: string;
  payment_reference: string;
  invoice_id: number;
  external_id: string;
  revolut_public_id?: string | null;
  revolut_environment?: 'production' | 'sandbox';
}

export const revolutApi = {
  topup: async (data: {
    advertiser_id: string;
    amount: number;
    return_url: string;
    save_payment_method?: boolean;
    description?: string;
  }): Promise<RevolutTopupResponse> => {
    const response = await api.post('/api/billing/revolut/topup', data);
    return response.data;
  },
  setupCard: async (data: {
    advertiser_id: string;
    return_url: string;
  }): Promise<{ setup_url: string; external_id: string; revolut_public_id?: string | null; revolut_environment?: 'production' | 'sandbox' }> => {
    const response = await api.post('/api/billing/revolut/setup-card', data);
    return response.data;
  },
  fxPreview: async (advertiserId: string, amount: number): Promise<{
    local_amount: number;
    local_currency: string;
    charge_amount?: number | null;
    charge_currency?: string | null;
    fx_rate?: number | null;
    fx_margin_pct?: number | null;
    needs_conversion: boolean;
  }> => {
    const response = await api.get('/api/billing/revolut/fx-preview', {
      params: { advertiser_id: advertiserId, amount },
    });
    return response.data;
  },
  chargeSaved: async (data: {
    advertiser_id: string;
    payment_method_id: number;
    amount: number;
    description?: string;
    browser_data?: Record<string, unknown>;
  }): Promise<{
    status: 'succeeded' | 'pending' | 'failed';
    payment_reference: string;
    invoice_id: number;
    external_id: string;
    failure_reason?: string | null;
  }> => {
    const response = await api.post('/api/billing/revolut/charge-saved', data);
    return response.data;
  },
};

// ─── Bank transfer (SEPA/SWIFT proforma) ─────────────────────

export interface BankTransferDetails {
  invoice_id: number;
  invoice_number: string;
  payment_reference: string;
  amount: number;
  currency: string;
  bank_details: {
    company_name: string | null;
    legal_address: string | null;
    vat_id: string | null;
    bank_iban: string | null;
    bank_swift: string | null;
  };
}

export const bankTransferApi = {
  createRequest: async (data: { advertiser_id: string; amount: number }): Promise<BankTransferDetails> => {
    const response = await api.post('/api/billing/bank-transfer/create-request', data);
    return response.data;
  },
};

// ─── Payment Profiles (admin, 2FA-gated) ─────────────────────────

export interface MerchantConfig {
  secret_key?: string | null;
  public_key?: string | null;
  api_base?: string | null;
  webhook_secret?: string | null;
}

export interface BusinessConfig {
  client_id?: string | null;
  jwt_iss?: string | null;
  api_base?: string | null;
  private_key_pem?: string | null;
  redirect_uri?: string | null;
  refresh_token?: string | null;
  webhook_secret?: string | null;
}

export interface BankDetails {
  company_name?: string | null;
  legal_address?: string | null;
  vat_id?: string | null;
  bank_iban?: string | null;
  bank_swift?: string | null;
  bank_name?: string | null;
  beneficiary?: string | null;
}

export interface SettlementAccount {
  id: string;
  currency: string;
  iban?: string | null;
  name?: string | null;
}

export type CardProvider = 'none' | 'revolut_merchant';
export type BankProvider = 'none' | 'manual' | 'revolut_business';

export interface PaymentProfile {
  id: number;
  name: string;
  provider_type: string;
  currency: string;
  is_active: boolean;
  card_provider: CardProvider;
  bank_provider: BankProvider;
  /** Currency the card is actually debited in. NULL = same as profile.currency.
   *  Used when the office accepts payments in a currency the gateway can't
   *  settle (e.g. KZT balance, EUR Revolut charge). */
  card_charge_currency?: string | null;
  merchant_config: MerchantConfig;     // values masked as '****'
  business_config: BusinessConfig;     // values masked as '****'
  bank_details: BankDetails;           // plaintext
  settlement_accounts: SettlementAccount[];
  created_at: string | null;
  updated_at: string | null;
  office_count?: number;
}

export interface PaymentProfileAuditEntry {
  id: number;
  user_email: string;
  action: string;
  profile_name: string;
  details: Record<string, any>;
  ip: string | null;
  created_at: string;
}

export const paymentProfilesApi = {
  list: async (): Promise<PaymentProfile[]> => {
    const response = await api.get('/api/admin/payment-profiles');
    return response.data;
  },
  get: async (id: number): Promise<PaymentProfile> => {
    const response = await api.get(`/api/admin/payment-profiles/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    currency: string;
    totp_code: string;
    card_provider?: CardProvider;
    bank_provider?: BankProvider;
    card_charge_currency?: string | null;
    merchant_config?: MerchantConfig;
    business_config?: BusinessConfig;
    bank_details?: BankDetails;
    settlement_accounts?: SettlementAccount[];
  }): Promise<PaymentProfile> => {
    const response = await api.post('/api/admin/payment-profiles', data);
    return response.data;
  },
  update: async (id: number, data: {
    totp_code: string;
    name?: string;
    currency?: string;
    is_active?: boolean;
    card_provider?: CardProvider;
    bank_provider?: BankProvider;
    card_charge_currency?: string | null;
    merchant_config?: MerchantConfig;
    business_config?: BusinessConfig;
    bank_details?: BankDetails;
    settlement_accounts?: SettlementAccount[];
  }): Promise<PaymentProfile> => {
    const response = await api.put(`/api/admin/payment-profiles/${id}`, data);
    return response.data;
  },
  remove: async (id: number, totp_code: string): Promise<void> => {
    await api.delete(`/api/admin/payment-profiles/${id}`, { data: { totp_code } });
  },
  audit: async (id: number): Promise<PaymentProfileAuditEntry[]> => {
    const response = await api.get(`/api/admin/payment-profiles/${id}/audit`);
    return response.data;
  },
  getFxMargin: async (): Promise<{ margin_pct: number }> => {
    const response = await api.get('/api/admin/payment-profiles/settings/fx-margin');
    return response.data;
  },
  setFxMargin: async (margin_pct: number, totp_code: string): Promise<{ margin_pct: number }> => {
    const response = await api.put('/api/admin/payment-profiles/settings/fx-margin', { margin_pct, totp_code });
    return response.data;
  },
};

// ─── Revolut Business one-time setup helpers ─────────────────────

export const revolutBusinessApi = {
  authorizeUrl: async (profileId: number): Promise<{ authorize_url: string }> => {
    const response = await api.get(`/api/billing/revolut-business/profile/${profileId}/authorize`);
    return response.data;
  },
  setupWebhook: async (profileId: number, url: string): Promise<any> => {
    const response = await api.post(`/api/billing/revolut-business/profile/${profileId}/setup-webhook`, { url });
    return response.data;
  },
  listAccounts: async (profileId: number): Promise<any> => {
    const response = await api.get(`/api/billing/revolut-business/profile/${profileId}/accounts`);
    return response.data;
  },
};

// ─── Budget Guard ──────────────────────────────────────────────

export interface PlatformSetting {
  key: string;
  value: string;
  description: string;
  category: string;
  value_type: string;
  updated_at: string;
  updated_by: number | null;
}

export interface BudgetGuardStatus {
  advertiser_id: string;
  db_balance: number;
  billing_model: string;
  is_paused_by_billing: boolean;
  auto_pause_enabled: boolean;
  redis: {
    balance: number;
    spent: number;
    spend_rate: number;
    estimated_ttl: number | null;
    status: string;
  };
}

export interface ValidationResult {
  allowed: boolean;
  anomaly_warning: {
    avg_daily_spend: number;
    new_daily_spend: number;
    deviation_x: number;
    threshold: number;
  } | null;
  balance_warning: {
    hourly_spend: number;
    required_balance: number;
    current_balance: number;
    coverage_hours: number;
    shortfall: number;
  } | null;
  details: Record<string, any>;
}

export const budgetGuardApi = {
  // Validation
  validateLaunch: async (data: {
    advertiser_id: string;
    campaign_budget: number;
    flight_days: number;
    force_confirm_anomaly?: boolean;
  }): Promise<ValidationResult> => {
    const response = await api.post('/api/budget-guard/validate-launch', data);
    return response.data;
  },

  // Platform settings
  getSettings: async (category?: string): Promise<{ settings: PlatformSetting[] }> => {
    const params = category ? `?category=${category}` : '';
    const response = await api.get(`/api/budget-guard/settings${params}`);
    return response.data;
  },

  updateSetting: async (key: string, value: string): Promise<any> => {
    const response = await api.put(`/api/budget-guard/settings/${key}`, { value });
    return response.data;
  },

  // Status & alerts
  getStatus: async (advertiserId: string): Promise<BudgetGuardStatus> => {
    const response = await api.get(`/api/budget-guard/status/${advertiserId}`);
    return response.data;
  },

  getAlerts: async (advertiserId?: string): Promise<{ alerts: any[] }> => {
    const params = advertiserId ? `?advertiser_id=${advertiserId}` : '';
    const response = await api.get(`/api/budget-guard/alerts${params}`);
    return response.data;
  },

  // Manual triggers
  reconcile: async (): Promise<any> => {
    const response = await api.post('/api/budget-guard/reconcile');
    return response.data;
  },

  initRedis: async (): Promise<any> => {
    const response = await api.post('/api/budget-guard/init-redis');
    return response.data;
  },
};

const apiService = new ApiService();
export default apiService;
export { api };
